import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

type Integration = {
  provider: string;
  label: string;
  implementation: string;
  configuration: string;
  connection: string;
  health: string;
  configured: boolean;
  accountLabel?: string | null;
  metadata?: Record<string, unknown>;
  security?: string;
  externalStepRequired?: boolean;
  externalUrl?: string;
  instructions?: string[];
};

const fields: Record<string, Array<{ key: string; label: string; type?: string; placeholder?: string }>> = {
  google_workspace: [
    { key: 'clientId', label: 'OAuth Client ID' },
    { key: 'clientSecret', label: 'OAuth Client Secret', type: 'password' },
  ],
  autentique: [{ key: 'apiToken', label: 'API Token', type: 'password' }, { key: 'webhookSecret', label: 'Webhook Secret', type: 'password' }],
  asaas: [{ key: 'environment', label: 'Ambiente', placeholder: 'sandbox ou production' }, { key: 'apiKey', label: 'API Key', type: 'password' }, { key: 'webhookToken', label: 'Webhook Token', type: 'password' }],
  whatsapp_cloud: [
    { key: 'accessToken', label: 'Access Token', type: 'password' },
    { key: 'phoneNumberId', label: 'Phone Number ID' },
    { key: 'businessAccountId', label: 'WhatsApp Business Account ID' },
    { key: 'appSecret', label: 'App Secret', type: 'password' },
    { key: 'verifyToken', label: 'Verify Token', type: 'password' },
    { key: 'graphApiVersion', label: 'Graph API Version', placeholder: 'v20.0' },
  ],
  banco_inter: [
    { key: 'environment', label: 'Ambiente', placeholder: 'sandbox ou production' },
    { key: 'clientId', label: 'Client ID' },
    { key: 'clientSecret', label: 'Client Secret', type: 'password' },
  ],
};

const statusClass = (value: string) => {
  if (['HEALTHY', 'CONNECTED', 'CONFIGURED', 'READY'].includes(value)) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (['FAILED', 'DEGRADED'].includes(value)) return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-amber-50 text-amber-700 border-amber-200';
};

export default function IntegrationConfigurationCenter() {
  const { user } = useAuth();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [readiness, setReadiness] = useState({ total: 0, configured: 0, connected: 0, pending: 0 });
  const [selected, setSelected] = useState<Integration | null>(null);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [accountLabel, setAccountLabel] = useState('');
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    if (user?.role !== 'admin') return;
    const response = await fetch('/api/integrations/configuration', { credentials: 'same-origin' });
    const body = await response.json();
    if (response.ok) {
      setIntegrations(body.integrations ?? []);
      setReadiness(body.readiness ?? { total: 0, configured: 0, connected: 0, pending: 0 });
    }
  }, [user?.role]);

  useEffect(() => { void load(); }, [load]);

  const currentFields = useMemo(() => fields[selected?.provider ?? ''] ?? [], [selected]);

  if (user?.role !== 'admin') {
    return (
      <div className="rounded-xl border border-background-200 bg-background-50 p-4 text-sm text-foreground-600">
        O Configuration Center administrativo está disponível somente para administradores. A saúde operacional continua disponível abaixo.
      </div>
    );
  }

  const open = (integration: Integration) => {
    setSelected(integration);
    setCredentials({});
    setAccountLabel(integration.accountLabel ?? '');
    setStep(0);
    setMessage(null);
  };

  const save = async () => {
    if (!selected) return;
    setBusy(true); setMessage(null);
    try {
      const response = await fetch(`/api/integrations/configuration/${selected.provider}`, {
        method: 'PUT', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credentials, accountLabel }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error ?? 'Não foi possível salvar a configuração.');
      setMessage({ ok: true, text: 'Configuração salva com segurança. Nenhum segredo é exibido novamente.' });
      setSelected(body.integration);
      await load();
      setStep(4);
    } catch (error) {
      setMessage({ ok: false, text: error instanceof Error ? error.message : 'Falha ao salvar.' });
    } finally { setBusy(false); }
  };

  const test = async () => {
    if (!selected) return;
    setBusy(true); setMessage(null);
    try {
      const response = await fetch(`/api/integrations/configuration/${selected.provider}`, { method: 'POST', credentials: 'same-origin' });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message ?? body.error ?? 'A conexão não foi validada.');
      setMessage({ ok: true, text: body.message ?? 'Conexão validada.' });
      await load();
      setSelected(body.integration ?? selected);
    } catch (error) { setMessage({ ok: false, text: error instanceof Error ? error.message : 'Falha no teste.' }); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!selected || !window.confirm(`Remover a configuração de ${selected.label}?`)) return;
    setBusy(true);
    try {
      const response = await fetch(`/api/integrations/configuration/${selected.provider}`, { method: 'DELETE', credentials: 'same-origin' });
      if (!response.ok) { const body = await response.json(); throw new Error(body.error ?? 'Não foi possível remover.'); }
      setSelected(null); await load();
    } catch (error) { setMessage({ ok: false, text: error instanceof Error ? error.message : 'Falha ao remover.' }); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-background-200 bg-white p-4 md:p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground-900">Configuration Center</h3>
          <p className="text-xs text-foreground-500 mt-1">Configure, teste e remova credenciais sem alterar código ou PowerShell.</p>
        </div>
        <div className="text-right text-xs text-foreground-600">
          <div><strong>{readiness.configured} de {readiness.total}</strong> configuradas · <strong>{readiness.connected}</strong> conectadas</div>
          <div className="text-foreground-400">Completude das integrações · saúde do core é independente</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {integrations.map((integration) => (
          <article key={integration.provider} className="rounded-xl border border-background-200 bg-background-50 p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div><h4 className="text-sm font-semibold text-foreground-900">{integration.label}</h4><p className="text-[11px] text-foreground-500 mt-1">Implementação: {integration.implementation}</p></div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${statusClass(integration.health)}`}>{integration.health}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div>Configuração<br /><span className={`inline-block mt-1 px-1.5 py-0.5 rounded border ${statusClass(integration.configuration)}`}>{integration.configuration}</span></div>
              <div>Conexão<br /><span className={`inline-block mt-1 px-1.5 py-0.5 rounded border ${statusClass(integration.connection)}`}>{integration.connection}</span></div>
            </div>
            {integration.externalStepRequired && <p className="text-[11px] text-amber-700">Etapa externa necessária: binding mTLS.</p>}
            <button type="button" onClick={() => open(integration)} className="w-full rounded-lg py-2 text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600">{integration.configured ? 'Gerenciar' : 'Configurar'}</button>
          </article>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl space-y-4">
            <div className="flex items-start justify-between gap-3"><div><h3 className="text-lg font-semibold text-foreground-900">{selected.label}</h3><p className="text-xs text-foreground-500">Informações · Pré-requisitos · Credenciais · Etapas externas · Teste · Ativação</p></div><button type="button" onClick={() => setSelected(null)} className="text-foreground-500">✕</button></div>
            <div className="flex flex-wrap gap-1.5 text-[10px]">{['Informações', 'Pré-requisitos', 'Credenciais', 'Etapas externas', 'Teste', 'Ativação'].map((label, index) => <span key={label} className={`rounded-full px-2 py-1 border ${index === step ? 'bg-primary-50 border-primary-200 text-primary-700' : 'border-background-200 text-foreground-500'}`}>{index + 1}. {label}</span>)}</div>
            {step === 0 && <div className="space-y-2 text-sm text-foreground-700"><p>Esta configuração é armazenada somente no servidor, criptografada com <code>INTEGRATION_MASTER_KEY</code>.</p><ul className="list-disc pl-5 text-xs space-y-1">{(selected.instructions ?? []).map((item) => <li key={item}>{item}</li>)}</ul>{selected.externalUrl && <a href={selected.externalUrl} target="_blank" rel="noreferrer" className="inline-block text-primary-600 underline">Abrir portal do fornecedor</a>}</div>}
            {step === 1 && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{selected.security === 'SECURITY_PREREQUISITE_REQUIRED' ? 'INTEGRATION_MASTER_KEY não está disponível neste ambiente. O administrador deve configurar esse segredo de infraestrutura antes de salvar credenciais.' : selected.externalStepRequired ? 'As credenciais podem ser salvas, mas o certificado mTLS deve existir como binding seguro da Cloudflare.' : 'Pré-requisitos do ambiente disponíveis.'}</div>}
            {step === 2 && <div className="space-y-3">{currentFields.map((field) => <label key={field.key} className="block text-sm text-foreground-700">{field.label}<input type={field.type ?? 'text'} value={credentials[field.key] ?? ''} placeholder={field.placeholder} onChange={(event) => setCredentials((current) => ({ ...current, [field.key]: event.target.value }))} className="mt-1 w-full rounded-lg border border-background-300 px-3 py-2 text-sm" autoComplete="off" /></label>)}<label className="block text-sm text-foreground-700">Rótulo da conta (opcional)<input value={accountLabel} onChange={(event) => setAccountLabel(event.target.value)} className="mt-1 w-full rounded-lg border border-background-300 px-3 py-2 text-sm" /></label></div>}
            {step === 3 && <div className="space-y-3 text-sm text-foreground-700"><p>{selected.provider === 'google_workspace' ? 'Cadastre no cliente Web do Google os redirect URIs exatos da aplicação antes de conectar Drive, Calendar ou Gmail.' : selected.provider === 'banco_inter' ? 'O certificado privado do Inter não deve ser colado aqui. Configure o binding mTLS externo e retorne para testar.' : 'Conclua a etapa externa no portal do fornecedor e depois use Testar conexão.'}</p>{selected.externalUrl && <a href={selected.externalUrl} target="_blank" rel="noreferrer" className="text-primary-600 underline">Abrir portal do fornecedor</a>}</div>}
            {step === 4 && <div className="space-y-3"><p className="text-sm text-foreground-600">O teste usa somente leitura quando o provider permite. Nenhuma mensagem ou cobrança é criada.</p><button type="button" onClick={() => void test()} disabled={busy || !selected.configured} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{busy ? 'Testando...' : 'Testar conexão'}</button></div>}
            {step === 5 && <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">Ativação concluída somente após teste bem-sucedido. Segredos nunca são retornados pela API.</div>}
            {message && <div className={`rounded-lg p-3 text-sm ${message.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>{message.text}</div>}
            <div className="flex flex-wrap justify-between gap-2 pt-2 border-t border-background-200"><button type="button" onClick={() => setStep((current) => Math.max(0, current - 1))} disabled={step === 0} className="rounded-lg border border-background-300 px-3 py-2 text-sm disabled:opacity-40">Anterior</button><div className="flex gap-2">{selected.configured && <button type="button" onClick={() => void remove()} disabled={busy} className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600">Remover configuração</button>}{step === 2 && <button type="button" onClick={() => void save()} disabled={busy} className="rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-white">{busy ? 'Salvando...' : 'Salvar configuração'}</button>}{step < 5 && step !== 2 && <button type="button" onClick={() => setStep((current) => Math.min(5, current + 1))} className="rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-white">Próximo</button>}</div></div>
          </div>
        </div>
      )}
    </section>
  );
}
